import { z } from 'zod';

/**
 * Payment on account. `org_accounts` holds the three settings staff control for every organization: how it pays,
 * its credit limit and whether its people see amounts. Only staff change them; customers learn the mode and
 * whether amounts are visible through `org_account_public`, never the limit.
 */
export const paymentModeSchema = z.enum(['card', 'account']);
export type PaymentMode = z.infer<typeof paymentModeSchema>;

/** The limit an org gets the first time it goes on account, unless staff chose one: 10,000.00 in its currency. */
export const DEFAULT_CREDIT_LIMIT_CENTS = 1_000_000;

/** A row of `public.org_accounts`, as staff read it (`staff.ledger.view` or `staff.account.manage`). */
export const orgAccountSchema = z.object({
  organization_id: z.uuid(),
  payment_mode: paymentModeSchema,
  /** Minor units of the org's currency; null is unlimited. */
  credit_limit_cents: z.number().int().positive().nullable(),
  /** Staff chose the limit (or unlimited), so the default is never applied over it. */
  credit_limit_set: z.boolean(),
  billing_visible: z.boolean(),
  /** Staff asked for account mode before the billing profile was complete; it applies once the profile is. */
  account_pending: z.boolean(),
  updated_by: z.uuid().nullable(),
  updated_at: z.iso.datetime({ offset: true }),
});
export type OrgAccount = z.infer<typeof orgAccountSchema>;

/** `org_account_public(org)`: the order form's view of the account. */
export const accountPublicSchema = z.object({
  payment_mode: paymentModeSchema,
  amounts_visible: z.boolean(),
});
export type AccountPublic = z.infer<typeof accountPublicSchema>;
